import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { Activity, ActivityType } from '@/types/lead';

// PERFORMANCE: Removed localStorage fallbacks - let errors propagate for proper handling
export const logActivity = async (activity: Omit<Activity, 'id' | 'createdAt'>): Promise<Activity> => {
  try {
    const newActivity = {
      id: uuidv4(),
      lead_id: activity.leadId,
      type: activity.type,
      title: activity.title,
      description: activity.description,
      metadata: activity.metadata,
      performed_by: activity.performedBy,
      created_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('activities')
      .insert(newActivity)
      .select()
      .single();

    if (error) throw error;

    return mapDbActivityToActivity(data);
  } catch (error) {
    console.warn('Failed to log activity, but continuing operation:', activity);
    throw error;
  }
};

// PERFORMANCE: Batch operation reduces database round trips
export const logActivitiesBatch = async (activities: Array<Omit<Activity, 'id' | 'createdAt'>>): Promise<Activity[]> => {
  try {
    if (activities.length === 0) {
      return [];
    }

    const newActivities = activities.map(activity => ({
      id: uuidv4(),
      lead_id: activity.leadId,
      type: activity.type,
      title: activity.title,
      description: activity.description,
      metadata: activity.metadata,
      performed_by: activity.performedBy,
      created_at: new Date().toISOString(),
    }));

    const { data, error } = await supabase
      .from('activities')
      .insert(newActivities)
      .select();

    if (error) throw error;

    return (data || []).map(mapDbActivityToActivity);
  } catch (error) {
    console.warn('Failed to log activities batch, but continuing operation');
    throw error;
  }
};

// Get activities for a lead
export const getActivitiesByLead = async (leadId: string): Promise<Activity[]> => {
  try {
    // Defensive check: ensure leadId is a valid string
    if (!leadId || typeof leadId !== 'string' || leadId === 'undefined') {
      console.warn('[ActivityService] Invalid leadId provided:', leadId);
      return [];
    }

    const { data, error } = await supabase
      .from('activities')
      .select(`
        *,
        profiles:performed_by (
          id,
          first_name,
          last_name,
          email,
          avatar_url
        )
      `)
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching activities for lead:', {
        leadId,
        error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    return (data || []).map(mapDbActivityToActivity);
  } catch (error) {
    console.error('Failed to fetch activities for lead:', leadId, error);
    // Return empty array instead of throwing - graceful degradation
    return [];
  }
};

// Helper function to map database activity to frontend Activity type
const mapDbActivityToActivity = (dbActivity: any): Activity => {
  const performedByName = dbActivity.profiles ? 
    `${dbActivity.profiles.first_name || ''} ${dbActivity.profiles.last_name || ''}`.trim() : 
    undefined;

  return {
    id: dbActivity.id,
    leadId: dbActivity.lead_id,
    type: dbActivity.type as ActivityType,
    title: dbActivity.title,
    description: dbActivity.description,
    metadata: dbActivity.metadata,
    performedBy: performedByName,
    createdAt: new Date(dbActivity.created_at),
  };
};

// Get activity type display information
export const getActivityTypeDisplay = (type: ActivityType) => {
  const types = {
    note: { label: 'Note', icon: 'MessageSquare' },
    status_change: { label: 'Status Change', icon: 'RefreshCw' },
    task: { label: 'Task', icon: 'CheckSquare' },
    document: { label: 'Document', icon: 'FileText' },
    call: { label: 'Call', icon: 'Phone' },
    email: { label: 'Email', icon: 'Mail' },
    meeting: { label: 'Meeting', icon: 'Calendar' },
    other: { label: 'Other', icon: 'MoreHorizontal' },
  };
  
  return types[type] || types.other;
};
